import { EmbedMetadata } from '@modules/url-metadata/EmbedMetadata';
import {
  Node,
  NodeViewProps,
  ParentConfig,
  callOrReturn,
  getExtensionField,
  mergeAttributes,
} from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import classNames from 'classnames';
import { ComponentType, memo } from 'react';
import { EmbedCommandOptions, EmbedRendererProps } from './types';

type EmbedExtensionOptions = {
  name: string;
  component: ComponentType<EmbedRendererProps<any>>;
};

const Component = memo(
  ({ node, updateAttributes, selected, extension }: NodeViewProps) => {
    if (!node.attrs?.href) return null;
    return (
      <NodeViewWrapper
        className={classNames({ selected })}
        key={node.attrs.href}
      >
        <extension.options.component
          selected={selected}
          url={node.attrs.href}
          metadata={node.attrs.metadata}
          onMetadataChange={(data: any) => {
            updateAttributes({
              metadata: data,
            });
          }}
        />
        <NodeViewContent />
      </NodeViewWrapper>
    );
  },
  (prev, next) =>
    prev.selected === next.selected &&
    prev.node.attrs.href === next.node.attrs.href &&
    JSON.stringify(prev.node.attrs.metadata) ===
      JSON.stringify(next.node.attrs.metadata),
);

declare module '@tiptap/core' {
  interface NodeConfig<Options, Storage> {
    /**
     * Add a command option to the node
     */
    bubbleMenu?:
      | null
      | boolean
      | ((this: {
          name: string;
          options: Options;
          storage: Storage;
          parent: ParentConfig<NodeConfig<Options>>['bubbleMenu'];
        }) => []);
  }
}

export const embedExtension = Node.create<EmbedExtensionOptions>({
  name: 'embed',
  group: 'block',
  selectable: true,
  atom: true,

  addOptions() {
    return {
      name: 'embed',
      component: EmbedMetadata,
    };
  },
  addAttributes() {
    return {
      href: {
        default: null,
      },
      metadata: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `embed[${this.options.name}]`,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'embed',
      mergeAttributes(HTMLAttributes, {
        [this.options.name]: '',
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component, {
      as: 'react-renderer',
    });
  },
  extendNodeSchema(extension) {
    const bubbleMenu = callOrReturn(
      getExtensionField(extension, 'bubbleMenu'),
      {
        name: extension.name,
        options: extension.options,
        storage: extension.storage,
      },
    );

    return {
      bubbleMenu,
    };
  },
  addCommands() {
    return {
      createEmbed:
        ({ href, range, type }) =>
        ({ chain }) =>
          chain()
            .insertContentAt(range, [{ type, attrs: { href } }])
            .createParagraphNear()
            .focus()
            .run(),
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      createEmbed: (
        options: EmbedCommandOptions & { type: string },
      ) => ReturnType;
    };
  }
}
